import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import type { ShiftSegment } from "@/features/dashboard/lib/panelData";

interface Props {
  /** Stops planned across the day — the bar's denominator. */
  total: number;
  /** Non-empty segments in draw order, from `buildShiftSegments`. */
  segments: ShiftSegment[];
  /** Hour ticks across the shift window, already formatted («08:30»). */
  hourLabels: string[];
  /**
   * Index into `hourLabels` of the tick nearest "now", or null when the
   * current time falls outside the shift window (before it opens, after it
   * closes) — then no tick is emphasised rather than the nearest edge being
   * mislabelled as «сейчас».
   */
  nowIndex: number | null;
  /** «сейчас 16:35 · до конца смены 55 мин», pre-resolved; null when unknown. */
  caption: string | null;
}

/**
 * Colour by MEANING, so fixed semantic tokens throughout (DEV_RULES §10.10).
 * «Осталось» is not an outcome — it is the untouched remainder — so it is the
 * neutral grey the rule reserves for structure and zero states.
 */
const SEGMENT_STYLES: Record<
  ShiftSegment["kind"],
  { bar: string; dot: string }
> = {
  delivered: { bar: "bg-brand-success", dot: "bg-brand-success" },
  late: { bar: "bg-brand-warning", dot: "bg-brand-warning" },
  undelivered: { bar: "bg-brand-danger", dot: "bg-brand-danger" },
  remaining: { bar: "bg-gray-200", dot: "bg-gray-200" },
};

/**
 * «ХОД СМЕНЫ» — one stacked bar across the shift window with a legend and an
 * hour axis marking «сейчас».
 *
 * The component renders only what it is GIVEN. It never reconstructs a missing
 * segment from a percentage: the owner's note is explicit that the late-stop
 * count must not be derived from `otd_percent`, whose denominator is a
 * different population (stops with a window AND an arrival). A segment with no
 * source is simply absent from `segments`, and the bar still sums to 100 %.
 */
export function ShiftTimeline({
  total,
  segments,
  hourLabels,
  nowIndex,
  caption,
}: Props) {
  const { t } = useTranslation();

  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
            {t("dashboard.shift.title")}
          </h3>
          {caption ? <p className="text-xs text-gray-500">{caption}</p> : null}
        </div>

        {/* Legend — one entry per segment actually drawn, so a segment with no
            backend source never appears in the key as a phantom zero.

            DYNAMIC i18n KEY below (`dashboard.shift.legend.${segment.kind}`).
            `check:i18n` scans STATIC string-literal t() calls only and is blind to
            this family, so the four keys are listed here and pinned by a
            RENDERED-TEXT test (`ShiftTimeline.test.tsx`), which is the only
            protection that actually works (DEV_RULES §10.9):
              dashboard.shift.legend.delivered
              dashboard.shift.legend.late
              dashboard.shift.legend.undelivered
              dashboard.shift.legend.remaining */}
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {segments.map((segment) => (
            <li key={segment.kind} className="flex items-center gap-1.5">
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  SEGMENT_STYLES[segment.kind].dot,
                )}
              />
              <span className="text-xs text-gray-500">
                {t(`dashboard.shift.legend.${segment.kind}`)}
              </span>
              <span className="text-xs font-semibold text-gray-700">
                {segment.count}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="img"
        aria-label={t("dashboard.shift.barLabel", { total, count: total })}
      >
        {segments.map((segment) => (
          // Width is a computed share, i.e. DATA — the project's established
          // pattern for a continuous length (see `RouteFactsMetricStrip`). The
          // colour stays a token.
          <div
            key={segment.kind}
            data-testid={`shift-segment-${segment.kind}`}
            data-pct={String(Math.round(segment.percent))}
            className={cn(
              "h-full transition-all",
              SEGMENT_STYLES[segment.kind].bar,
            )}
            style={{ width: `${segment.percent}%` }}
          />
        ))}
      </div>

      {/* Hour axis. On a phone only the first, «сейчас» (or middle) and last
          labels survive — six ticks at 400px overlap into noise. */}
      {hourLabels.length > 0 && (
        <ul className="mt-2 flex items-center justify-between">
          {hourLabels.map((label, index) => {
            const isNow = nowIndex === index;
            const isEdge = index === 0 || index === hourLabels.length - 1;
            return (
              <li
                key={`${label}-${index}`}
                className={cn(
                  "text-[11px] tabular-nums",
                  isNow ? "font-bold text-gray-700" : "text-gray-400",
                  // Narrow screens keep only the two edges and «сейчас».
                  !isEdge && !isNow && "hidden sm:block",
                )}
              >
                {label}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
